import classNames from "classnames";
import Datasource, { DatasourceResponse } from "lib/datasource";
import moment from "moment";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";

interface ReasonCode {
  id: string;
  label: string;
  is_controlled?: boolean;
  use_count?: number;
}

interface Props {
  value?: string;
  /**
   * Fires on every change of intent:
   *   (id, label)   — an existing reason was picked, or a new one was created here
   *   (null, text)  — free text typed but not confirmed. The text is a PENDING
   *                   label: the parent may submit it and let the server resolve
   *                   it (ReasonCode.find_or_create_from_text is idempotent on
   *                   lower(label)), so the user never has to click the
   *                   "Add as new reason" row.
   *   (null, "")    — the field is empty; nothing selected, nothing pending.
   */
  onChange: (reasonCodeId: string | null, label?: string) => void;
}

// Both the "Add as new reason" row and the parent's implicit create use this
// threshold, so the row appearing is exactly the signal that the typed text is
// long enough to become a reason code on submit.
export const MIN_REASON_LENGTH = 3;

const ReasonCodeAutocomplete = ({ value, onChange }: Props) => {
  const [suggestions, setSuggestions] = useState<ReasonCode[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Cached controlled list for the "show all on focus" feature (Req 1).
  // allFetchedRef is a synchronous guard (like isCreatingRef) so a second
  // focus event never fires a duplicate no-q GET.
  const [allOptions, setAllOptions] = useState<ReasonCode[]>([]);
  const allFetchedRef = useRef(false);

  const [, renderTrigger] = useState(moment());
  const [autocompleteDatasource] = useState(
    new Datasource({ mainModelName: "reason_code", renderTrigger: renderTrigger })
  );
  const [createDatasource] = useState(
    new Datasource({ mainModelName: "reason_code", renderTrigger: renderTrigger })
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Blocker fix 1 — stale-response race.
  // Each GET call atomically increments this counter and captures its own id in
  // a Promise closure. When the Promise resolves, we compare the captured id to
  // the ref. If a newer GET has been dispatched since, the captured id will be
  // smaller than the ref and the response is discarded as stale. This does not
  // require any changes to the Datasource layer.
  // NOTE: the no-q focus fetch also uses this same seq counter so a slow
  // show-all response cannot clobber a newer typed-search response.
  const requestSeqRef = useRef(0);

  // Blocker fix 2 — duplicate-creation on double-click.
  // React setState is async; two same-tick onMouseDown events both read
  // isCreating===false from their stale render closure. A ref is updated
  // synchronously in handleAddNew before the first POST fires, so a second
  // same-tick event sees isCreatingRef.current===true and returns early.
  const isCreatingRef = useRef(false);

  // Fetch-all-options on first focus — retrieves the controlled list once and
  // caches it for the component's lifetime (allFetchedRef prevents re-fetch).
  const fetchAllOptions = useCallback(() => {
    if (allFetchedRef.current) return;
    allFetchedRef.current = true;
    const seqAtDispatch = ++requestSeqRef.current;
    autocompleteDatasource
      .get("v2", "reason_codes", { queryStringParameters: { limit: "50" } })
      .then((response: DatasourceResponse) => {
        if (seqAtDispatch !== requestSeqRef.current) return; // stale guard
        if (response && response.success !== false) {
          const list = response.normalizedMainModelResponse;
          const arr = Array.isArray(list) ? list : [];
          setAllOptions(arr);
          setSuggestions(arr);
          setIsOpen(true);
        }
      })
      .catch(() => {
        // Allow retry on failure by resetting the flag
        allFetchedRef.current = false;
      });
  }, [autocompleteDatasource]);

  // Subscribe to create datasource
  useEffect(() => {
    const sub = createDatasource.responseSubject$.subscribe(
      (response: DatasourceResponse) => {
        if (response.success) {
          const code: ReasonCode = response.normalizedMainModelResponse;
          isCreatingRef.current = false;
          setIsCreating(false);
          setInputValue(code.label || "");
          setIsOpen(false);
          onChange(code.id, code.label);
        } else {
          isCreatingRef.current = false;
          setIsCreating(false);
        }
      }
    );
    return () => sub.unsubscribe();
  }, [createDatasource, onChange]);

  // Close list when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear debounce timer on unmount to avoid stray network requests
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value;
      setInputValue(q);
      // Drop any previously selected id, but hand the parent the raw text as a
      // PENDING label so it can create-on-submit. Passing `undefined` here (as
      // this did originally) meant typed-but-unclicked text was invisible to the
      // parent and the submit button stayed disabled until the user clicked the
      // "Add as new reason" row.
      onChange(null, q);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (q.length >= 3) {
        debounceRef.current = setTimeout(() => {
          // Capture this request's sequence id. If a newer request is dispatched
          // before this Promise resolves, seqAtDispatch < requestSeqRef.current
          // and we discard the stale response.
          const seqAtDispatch = ++requestSeqRef.current;
          autocompleteDatasource
            .get("v2", "reason_codes", {
              queryStringParameters: { q, limit: "10" },
            })
            .then((response: DatasourceResponse) => {
              // Stale-response guard: ignore if a newer GET has been dispatched
              if (seqAtDispatch !== requestSeqRef.current) return;
              if (response && response.success !== false) {
                const list = response.normalizedMainModelResponse;
                setSuggestions(Array.isArray(list) ? list : []);
                setIsOpen(true);
              }
            })
            .catch(() => {
              // Network errors are surfaced via the datasource notification layer;
              // no additional handling needed here.
            });
        }, 250);
      } else {
        // Below 3 chars: restore the cached controlled list if available
        // (decision #3 — keep showing the full list rather than closing).
        if (allFetchedRef.current) {
          setSuggestions(allOptions);
          setIsOpen(true);
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
      }
    },
    [autocompleteDatasource, onChange, allOptions]
  );

  const handleSelectSuggestion = useCallback(
    (code: ReasonCode) => {
      setInputValue(code.label);
      setIsOpen(false);
      onChange(code.id, code.label);
    },
    [onChange]
  );

  const handleAddNew = useCallback(() => {
    if (!inputValue.trim()) return;
    // Synchronous double-submit guard — isCreatingRef is updated before the POST
    // fires, so two same-tick onMouseDown events cannot both reach the POST call.
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setIsCreating(true);
    createDatasource.post(
      "v2",
      "reason_codes",
      { body: { reason_code: { label: inputValue.trim() } } },
      { updateIsLoding: false, displayNotification: false }
    );
  }, [createDatasource, inputValue]);

  const exactMatch =
    inputValue.trim().length > 0 &&
    suggestions.some(
      (s) => s.label.toLowerCase() === inputValue.trim().toLowerCase()
    );

  const highlightMatch = (label: string, query: string) => {
    if (!query || query.length < 3) return <span>{label}</span>;
    const lowerLabel = label.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const idx = lowerLabel.indexOf(lowerQuery);
    if (idx === -1) return <span>{label}</span>;
    return (
      <span>
        {label.slice(0, idx)}
        <span className="text-primary font-semibold">
          {label.slice(idx, idx + query.length)}
        </span>
        {label.slice(idx + query.length)}
      </span>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="w-full border border-black-20 rounded px-3 py-2 text-14 focus:outline-none focus:border-primary"
        placeholder="Click to see options or type to search…"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (inputValue.trim().length >= 3) {
            // Already in typed-search mode — re-open if results are cached
            if (suggestions.length > 0) setIsOpen(true);
          } else if (allFetchedRef.current) {
            // Cache is warm — show the controlled list immediately
            setSuggestions(allOptions);
            setIsOpen(true);
          } else {
            // First focus — fire the no-q GET and open on resolve
            fetchAllOptions();
          }
        }}
      />
      {isOpen && (
        <div className="absolute z-50 w-full bg-white border border-black-20 rounded shadow-lg mt-1 max-h-60 overflow-y-auto">
          {suggestions.map((code) => (
            <div
              key={code.id}
              className="flex items-center justify-between px-3 py-2 hover:bg-black-5 cursor-pointer text-black-80"
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectSuggestion(code);
              }}
            >
              <span className="text-14">
                {highlightMatch(code.label, inputValue)}
              </span>
              {typeof code.use_count === "number" && code.use_count > 0 && (
                <span className="text-12 text-black-40 ml-3 flex-shrink-0">
                  Used {code.use_count}x
                </span>
              )}
            </div>
          ))}
          {!exactMatch && inputValue.trim().length >= MIN_REASON_LENGTH && (
            <div
              className={classNames(
                "px-3 py-2 text-14 text-primary cursor-pointer hover:bg-black-5 border-t border-black-20",
                { "opacity-50 pointer-events-none": isCreating }
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddNew();
              }}
            >
              {isCreating
                ? "Adding…"
                : `Add as new reason: "${inputValue.trim()}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(ReasonCodeAutocomplete);
